import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputBase,
  IconButton,
  Switch,
  TextField,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import ExcelJS from "exceljs";

import { tokens } from "../../theme";
import Header from "../../components/Header";
import CustomToolbar from "../../utils/CustomToolbar";
import {
  getVacacionesView,
  postVacacionesUpdate,
} from "../../services/vacaciones.services";

import ModalCharge from "../../modal/modalCharge";
import ModalSucces from "../../modal/modalSucces";
import ModalError from "../../modal/modalError";

const VacacionesView = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // States
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [hadVacationMap, setHadVacationMap] = useState({});
  const [departureDates, setDepartureDates] = useState({});
  const [returnDates, setReturnDates] = useState({});

  // Modal states
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState("");

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "id_usuario", headerName: "ID_USUARIO", flex: 0.7 },
    {
      field: "nombre",
      headerName: "NOMBRE",
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "worked_days",
      headerName: "Días Trabajados",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "had_vacation",
      headerName: "¿Tuvo Vacaciones?",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const idUsuario = params.row.id_usuario;
        const checked = hadVacationMap[idUsuario] === "yes";

        const handleChange = (event) => {
          const isChecked = event.target.checked;
          setHadVacationMap((prev) => ({
            ...prev,
            [idUsuario]: isChecked ? "yes" : "no",
          }));

          if (!isChecked) {
            setDepartureDates((prev) => ({ ...prev, [idUsuario]: null }));
            setReturnDates((prev) => ({ ...prev, [idUsuario]: null }));
          }
        };

        return (
          <Switch
            checked={checked}
            onChange={handleChange}
            color="primary"
            inputProps={{ "aria-label": `had-vacation-switch-${idUsuario}` }}
          />
        );
      },
    },
    {
      field: "departure_date",
      headerName: "Fecha de Salida",
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const idUsuario = params.row.id_usuario;
        const value = departureDates[idUsuario] || null;
        const disabled = hadVacationMap[idUsuario] !== "yes";

        return (
          <DatePicker
            disabled={disabled}
            value={value}
            onChange={(newValue) =>
              setDepartureDates((prev) => ({ ...prev, [idUsuario]: newValue }))
            }
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: "100%" }} />
            )}
          />
        );
      },
    },
    {
      field: "return_date",
      headerName: "Fecha de Regreso",
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const idUsuario = params.row.id_usuario;
        const value = returnDates[idUsuario] || null;
        const disabled = hadVacationMap[idUsuario] !== "yes";

        return (
          <DatePicker
            disabled={disabled}
            value={value}
            onChange={(newValue) =>
              setReturnDates((prev) => ({ ...prev, [idUsuario]: newValue }))
            }
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: "100%" }} />
            )}
          />
        );
      },
    },
  ];

  // Initialize state from backend values
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const permisos = await getVacacionesView();

        setData(permisos);
        setFilteredData(permisos);

        const initialHadVacation = {};
        const initialDepartureDates = {};
        const initialReturnDates = {};
        permisos.forEach((user) => {
          initialHadVacation[user.id_usuario] = user.tuvo_vacaciones === 1 ? "yes" : "no";
          initialDepartureDates[user.id_usuario] = user.fecha_salida_vacaciones
            ? dayjs(user.fecha_salida_vacaciones)
            : null;
          initialReturnDates[user.id_usuario] = user.fecha_regreso_vacaciones
            ? dayjs(user.fecha_regreso_vacaciones)
            : null;
        });
        setHadVacationMap(initialHadVacation);
        setDepartureDates(initialDepartureDates);
        setReturnDates(initialReturnDates);
      } catch (error) {
        console.error("Error fetching permisos:", error);
        setSaveErrorMessage("Error al cargar los datos");
        setSaveError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) =>
        item.nombre.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchValue, data]);

  const handleExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Permisos");

      worksheet.columns = [
        { header: "ID", key: "id", width: 10 },
        { header: "ID_USUARIO", key: "id_usuario", width: 15 },
        { header: "NOMBRE", key: "nombre", width: 30 },
        { header: "Días Trabajados", key: "worked_days", width: 15 },
        { header: "¿Tuvo Vacaciones?", key: "had_vacation", width: 15 },
        { header: "Fecha de Salida", key: "departure_date", width: 20 },
        { header: "Fecha de Regreso", key: "return_date", width: 20 },
      ];

      const exportableData = filteredData.map((row) => ({
        id: row.id,
        id_usuario: row.id_usuario,
        nombre: row.nombre,
        worked_days: row.worked_days,
        had_vacation: hadVacationMap[row.id_usuario] || "no",
        departure_date: departureDates[row.id_usuario]
          ? dayjs(departureDates[row.id_usuario]).format("YYYY-MM-DD")
          : "",
        return_date: returnDates[row.id_usuario]
          ? dayjs(returnDates[row.id_usuario]).format("YYYY-MM-DD")
          : "",
      }));

      worksheet.addRows(exportableData);

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF143F04" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber !== 1) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Permisos_${dayjs().format("YYYYMMDD")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      setSaveErrorMessage("Error al exportar el archivo Excel");
      setSaveError(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = Object.entries(hadVacationMap).map(([userId, hadVacation]) => ({
        id_usuario: userId,
        tuvo_vacaciones: hadVacation === "yes" ? 1 : 0,
        fecha_salida_vacaciones:
          hadVacation === "yes" && departureDates[userId]
            ? dayjs(departureDates[userId]).format("YYYY-MM-DD")
            : null,
        fecha_regreso_vacaciones:
          hadVacation === "yes" && returnDates[userId]
            ? dayjs(returnDates[userId]).format("YYYY-MM-DD")
            : null,
      }));

      await postVacacionesUpdate(updates);
      setSaveSuccess(true);

      // Re-fetch and re-initialize after saving
      const permisos = await getVacacionesView();

      setData(permisos);
      setFilteredData(permisos);

      const initialHadVacation = {};
      const initialDepartureDates = {};
      const initialReturnDates = {};
      permisos.forEach((user) => {
        initialHadVacation[user.id_usuario] = user.tuvo_vacaciones === 1 ? "yes" : "no";
        initialDepartureDates[user.id_usuario] = user.fecha_salida_vacaciones
          ? dayjs(user.fecha_salida_vacaciones)
          : null;
        initialReturnDates[user.id_usuario] = user.fecha_regreso_vacaciones
          ? dayjs(user.fecha_regreso_vacaciones)
          : null;
      });
      setHadVacationMap(initialHadVacation);
      setDepartureDates(initialDepartureDates);
      setReturnDates(initialReturnDates);
    } catch (error) {
      console.error("Error saving vacation data:", error);
      setSaveErrorMessage(error.message || "Error al guardar los datos");
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box m="20px">
        <Box display="flex" justifyContent="space-between" p={0}>
          <Header title="Vacaciones" subtitle="Listado de vacaciones" />
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              sx={{
                background: "#143f04",
                color: "white",
                borderRadius: "8px",
                height: "3rem",
                "&:hover": { background: "#36a60f" },
              }}
              onClick={handleExport}
              variant="contained"
            >
              Exportar
            </Button>
            <Button
              sx={{
                background: "#007bff",
                color: "white",
                borderRadius: "8px",
                height: "3rem",
                "&:hover": { background: "#0056b3" },
              }}
              onClick={handleSave}
              variant="contained"
              disabled={isSaving}
            >
              {isSaving ? "Guardando..." : "Guardar"}
            </Button>
            <Box
              display="flex"
              backgroundColor={colors.primary[400]}
              borderRadius="3px"
              height={"50%"}
              width={"100%"}
              ml={1}
            >
              <InputBase
                sx={{ ml: 2, flex: 1 }}
                placeholder="Buscar por nombre"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <IconButton type="button" sx={{ p: 1 }}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box
          m="40px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .name-column--cell": { color: colors.greenAccent[300] },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[900],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            rows={filteredData}
            columns={columns}
            getRowId={(row) => row.id}
            components={{ Toolbar: CustomToolbar }}
            pageSize={100}
            rowsPerPageOptions={[100]}
            autoHeight={false}
          />
        </Box>

        <ModalCharge isLoading={isLoading || isSaving} />
        <ModalSucces open={saveSuccess} onClose={() => setSaveSuccess(false)} />
        <ModalError
          open={saveError}
          onClose={() => setSaveError(false)}
          error={saveErrorMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default VacacionesView;